import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Truck, BatteryCharging, Disc, Fuel, Lock, Anchor, Zap, Shield, Clock, Mail, Phone, MapPin, Smartphone, MessageSquare } from "lucide-react";
import MobileHelpFab from "@/components/MobileHelpFab";

const ICON_MAP = { Truck, BatteryCharging, Disc, Fuel, Lock, Anchor };

export default function Landing() {
  const [publicServices, setPublicServices] = useState([]);
  useEffect(() => {
    api.get("/services/public").then(({ data }) => setPublicServices(data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Mobile: quick-access help lives in the floating action button (bottom-right). See MobileHelpFab. */}
      <MobileHelpFab requestPath="/register" requestLabel="Request Service" />

      {/* Top bar */}
      <header className="sticky top-0 sm:top-0 z-40 bg-white/90 backdrop-blur border-b-2 border-asap-deep">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0"><Logo size={72} /></div>
            <div className="hidden xl:block ml-2 pl-3 border-l-2 border-asap-orange shrink" data-testid="service-area-badge">
              <div className="text-[9px] uppercase tracking-[0.3em] font-bold text-asap-orange">Now Serving</div>
              <div className="text-sm font-display uppercase text-asap-deep leading-tight">USA Nationwide · 24/7 Rescue Network</div>
            </div>
          </div>
          <nav className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link to="/get-app" data-testid="nav-get-app" className="hidden lg:inline-flex items-center gap-1 px-3 py-2 font-bold uppercase text-xs sm:text-sm tracking-wider text-asap-orange hover:text-asap-deep transition">
              <Smartphone size={14}/> Get App
            </Link>
            <a href="#pricing" data-testid="nav-pricing" className="hidden md:inline-block px-3 py-2 font-bold uppercase text-xs sm:text-sm tracking-wider text-asap-deep hover:text-asap-orange transition">Pricing</a>
            <a href="#about" data-testid="nav-about" className="hidden lg:inline-block px-3 py-2 font-bold uppercase text-xs sm:text-sm tracking-wider text-asap-deep hover:text-asap-orange transition">About</a>
            <Link to="/login" data-testid="nav-login" className="px-2 sm:px-4 py-2 font-bold uppercase text-xs sm:text-sm tracking-wider text-asap-deep hover:text-asap-orange transition">Log In</Link>
            <Link to="/register" data-testid="nav-register" className="px-3 sm:px-5 py-2 bg-asap-yellow text-asap-deep font-bold uppercase text-xs sm:text-sm tracking-wider border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all whitespace-nowrap">
              Get Help Now
            </Link>
          </nav>
        </div>
        {/* Show service area on tablet & mobile as a full-width strip; desktop >= xl gets the inline badge */}
        <div className="xl:hidden px-4 py-2 bg-asap-deep text-white text-center" data-testid="service-area-mobile">
          <div className="text-[9px] uppercase tracking-[0.3em] font-bold text-asap-yellow">Now Serving</div>
          <div className="text-xs font-bold uppercase leading-tight">USA Nationwide · 24/7 Rescue Network</div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #061224 0%, #0B3087 60%, #E62E2D 130%)" }}>
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-asap-yellow/20 border border-asap-yellow text-asap-yellow text-xs uppercase tracking-[0.3em] font-bold mb-6">
              <Zap size={14} /> 24/7 Roadside Rescue
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] uppercase">
              Stuck on the road?
              <span className="block text-asap-yellow mt-2">We sprint to you.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl">
              Tow, jump, tire, fuel, lockout, winch, or ride — one tap and the
              fastest roadrunner in dispatch is on the way.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" data-testid="hero-cta-customer" className="px-7 py-4 bg-asap-yellow text-asap-deep font-display uppercase text-base tracking-wide border-2 border-asap-yellow shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all">
                Request Service →
              </Link>
              <Link to="/register" data-testid="hero-cta-driver" state={{ role: "driver" }} className="px-7 py-4 bg-transparent text-white border-2 border-white font-display uppercase text-base tracking-wide hover:bg-white hover:text-asap-deep transition">
                Drive with Us
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4">
              <Stat icon={Clock} label="Live ETA" value="After Match" />
              <Stat icon={Shield} label="Vetted" value="Insured Pros" />
              <Stat icon={Zap} label="Active 24/7" value="365d" />
            </div>
          </div>

          <div className="relative">
            <div className="aspect-square max-w-md mx-auto bg-asap-yellow/10 backdrop-blur-md border-2 border-asap-yellow/30 rounded-3xl p-8 grid grid-cols-3 gap-3">
              {publicServices.slice(0, 6).map((s) => {
                const Icon = ICON_MAP[s.icon] || Truck;
                return (
                  <div key={s.id} className="bg-white/95 rounded-md p-4 flex flex-col items-center justify-center text-asap-deep border-2 border-asap-deep" data-testid={`hero-svc-${s.id}`}>
                    <Icon size={26} strokeWidth={2.5} className="text-asap-orange" />
                    <div className="text-[10px] mt-2 font-bold uppercase tracking-wider text-center leading-tight">{s.name}</div>
                    <div className="text-[10px] font-display text-asap-deep mt-0.5">${s.price.toFixed(0)}</div>
                  </div>
                );
              })}
              {publicServices.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white/40 rounded-md p-4 border-2 border-white/30 animate-pulse"/>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Public Services & Pricing — server-authoritative */}
      <section id="pricing" className="bg-white border-t-4 border-asap-yellow">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">Services & Pricing</div>
          <h2 className="font-display text-3xl sm:text-5xl uppercase text-asap-deep mt-2 max-w-2xl">Flat local pricing. No surprises.</h2>
          <p className="mt-3 text-slate-600 text-sm max-w-2xl">These are the same live prices used at checkout. Tips go 100% to your driver. Towing miles beyond hook-up may apply.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-pricing-grid">
            {publicServices.map((s) => {
              const Icon = ICON_MAP[s.icon] || Truck;
              return (
                <div key={s.id} className="p-5 border-2 border-asap-deep bg-white shadow-hard-sm" data-testid={`price-card-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <Icon size={28} className="text-asap-orange"/>
                    <div className="font-display text-3xl text-asap-deep">${s.price.toFixed(2)}</div>
                  </div>
                  <div className="font-display uppercase text-asap-deep text-lg mt-2">{s.name}</div>
                  <div className="text-xs text-slate-600 mt-1">{s.desc}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-[11px] text-slate-500">Ride / Passenger Transport is temporarily unavailable — call dispatch if you need one arranged.</div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">How it works</div>
        <h2 className="font-display text-3xl sm:text-5xl uppercase text-asap-deep mt-2 max-w-2xl">Three taps. The roadrunner sprints. You're rolling again.</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Pick a service", d: "Tow, jump, tire, fuel, lockout, winch, or a ride. Server-set fair pricing." },
            { n: "02", t: "Drop the pin", d: "Tap your spot on the map. Add vehicle info and any notes for the driver." },
            { n: "03", t: "We're on the way", d: "A nearby driver hears the vroom alert and dispatches in seconds." },
          ].map((s) => (
            <div key={s.n} className="bg-white border-2 border-asap-deep p-6 shadow-hard">
              <div className="font-display text-5xl text-asap-orange">{s.n}</div>
              <div className="font-display text-xl uppercase text-asap-deep mt-2">{s.t}</div>
              <p className="text-slate-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About / Contact */}
      <section id="about" className="bg-white border-t-4 border-asap-yellow">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-asap-orange font-bold">About Us</div>
            <h2 className="font-display text-3xl sm:text-5xl uppercase text-asap-deep mt-2">Built by drivers, for drivers.</h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              ASAP Roadside — <span className="font-bold text-asap-deep">Roadrunner Rescue</span> — is a 24/7 rescue network operated by
              <span className="font-bold text-asap-deep"> ASAP Our's 24-7 Network Services</span>. We sprint to
              stranded motorists across our service zones with tows, jumps, tire changes, fuel drops, lockouts, winch-outs, and rides —
              all through one clean app, at fair server-set pricing.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Every driver on our platform is vetted, insured, and matched to jobs their rig can physically handle,
              so you never wait on the wrong truck. Fast. Fair. Local. That's the promise.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              <MiniStat label="24/7" sub="Coverage"/>
              <MiniStat label="Vetted" sub="Drivers"/>
              <MiniStat label="Fair" sub="Server pricing"/>
            </div>
          </div>

          <div className="bg-gradient-to-br from-asap-deep to-asap-blue text-white p-8 sm:p-10 border-2 border-asap-deep shadow-hard" data-testid="contact-section">
            <div className="text-xs tracking-[0.3em] uppercase text-asap-yellow font-bold">Get In Touch</div>
            <h3 className="font-display text-3xl sm:text-4xl uppercase mt-2">Contact us</h3>
            <p className="mt-3 text-white/80">Questions, partnerships, press, or urgent support — the roadrunner's inbox is always open.</p>
            <div className="mt-8 space-y-5">
              <ContactRow icon={Mail} label="Business Email" testid="contact-email">
                <a href="mailto:asaproadrunners@rs-asap.com" className="text-asap-yellow font-bold hover:underline break-all">asaproadrunners@rs-asap.com</a>
              </ContactRow>
              <ContactRow icon={Phone} label="Dispatch Phone" testid="contact-phone">
                <a href="tel:+18317477141" className="text-asap-yellow font-bold hover:underline">831-747-7141</a>
                <div className="text-white/60 text-xs mt-1">Voice calls only</div>
              </ContactRow>
              <ContactRow icon={MessageSquare} label="Text Line" testid="contact-text">
                <a href="sms:+17653301951" className="text-asap-yellow font-bold hover:underline">765-330-1951</a>
                <div className="text-white/60 text-xs mt-1">Text messages only</div>
              </ContactRow>
              <ContactRow icon={MapPin} label="Coverage" testid="contact-coverage">
                <span className="text-white/90">USA nationwide · Indianapolis, Muncie & every US zone</span>
              </ContactRow>
              <ContactRow icon={Clock} label="Hours" testid="contact-hours">
                <span className="text-white/90">24 hours · 7 days · 365 nights</span>
              </ContactRow>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="about-cta-signup" className="px-5 py-3 bg-asap-yellow text-asap-deep font-display uppercase text-sm border-2 border-asap-yellow shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all">Get Started</Link>
              <Link to="/faq" data-testid="about-cta-faq" className="px-5 py-3 border-2 border-white text-white font-display uppercase text-sm hover:bg-white hover:text-asap-deep transition">FAQ</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-asap-deep text-white py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-5xl uppercase">
            Need help <span className="text-asap-yellow">right now?</span>
          </h2>
          <p className="text-white/70 mt-3">Create an account in under 30 seconds.</p>
          <Link to="/register" data-testid="footer-cta" className="inline-block mt-6 px-8 py-4 bg-asap-yellow text-asap-deep font-display uppercase border-2 border-asap-yellow shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all">
            Get Started Free
          </Link>
        </div>
      </section>

      <footer className="bg-asap-deep border-t border-white/10 text-white/60 text-xs py-6 text-center">
        © {new Date().getFullYear()} ASAP Our's 24-7 Network Services · Roadrunner Rescue.{" "}
        <Link to="/legal" data-testid="footer-legal" className="text-asap-yellow hover:underline font-bold uppercase tracking-wider">Legal Library</Link>
        {" · "}
        <Link to="/faq" data-testid="footer-faq" className="text-asap-yellow hover:underline font-bold uppercase tracking-wider">Help</Link>
        {" · "}
        <Link to="/privacy-request" data-testid="footer-privacy" className="text-asap-yellow hover:underline font-bold uppercase tracking-wider">Privacy Rights</Link>
        {" · "}
        <a href="mailto:asaproadrunners@rs-asap.com" className="text-asap-yellow hover:underline">asaproadrunners@rs-asap.com</a>
        {" · "}
        <a href="tel:+18317477141" className="text-asap-yellow hover:underline">Call 831-747-7141</a>
        {" · "}
        <a href="sms:+17653301951" className="text-asap-yellow hover:underline">Text 765-330-1951</a>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border-l-2 border-asap-yellow pl-3 min-w-0">
      <div className="flex items-center gap-1.5 text-asap-yellow text-[10px] sm:text-xs uppercase tracking-widest font-bold"><Icon size={14} className="shrink-0"/> <span className="truncate">{label}</span></div>
      <div className="font-display text-lg sm:text-2xl mt-1 leading-tight break-words">{value}</div>
    </div>
  );
}

function MiniStat({ label, sub }) {
  return (
    <div className="border-2 border-asap-deep p-3 sm:p-4 text-center bg-asap-yellow/10 min-w-0 overflow-hidden" data-testid={`ministat-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="font-display text-sm sm:text-2xl uppercase text-asap-deep leading-tight whitespace-nowrap truncate">{label}</div>
      <div className="text-[9px] sm:text-[10px] tracking-widest uppercase text-slate-600 font-bold mt-1">{sub}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <div className="w-11 h-11 bg-asap-yellow text-asap-deep flex items-center justify-center border-2 border-asap-yellow shrink-0">
        <Icon size={20} strokeWidth={2.5}/>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.25em] uppercase text-asap-yellow font-bold">{label}</div>
        <div className="mt-1">{children}</div>
      </div>
    </div>
  );
}
